import { Descriptions, Modal, Tag, Typography, Space, Divider } from 'antd';
import moment from 'moment';

import type { ModelNeuralNetworkLogsResult } from '@app/api/NeuralNetworkLogs';

type Props = {
  open: boolean;
  log: ModelNeuralNetworkLogsResult | null;
  onClose: () => void;
};

const tryPretty = (text?: ModelNeuralNetworkLogsResult['Request']) => {
  const raw = text ?? '';
  try {
    const json = JSON.parse(raw);
    return JSON.stringify(json, null, 2);
  } catch {
    return raw;
  }
};

const StatusPill = ({
  status,
}: {
  status?: ModelNeuralNetworkLogsResult['Status'];
}) => {
  const s = (status ?? '').toLowerCase();
  const color =
    s === 'success'
      ? 'green'
      : s === 'error'
      ? 'red'
      : s === 'fail'
      ? 'red'
      : 'default';
  return <Tag color={color}>{status ?? '-'}</Tag>;
};

const Pre = ({ text }: { text?: string }) => (
  <Typography.Paragraph
    copyable={{ text: text ?? '' }}
    style={{ marginBottom: 0 }}
  >
    <pre
      style={{
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontFamily:
          'ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono","Courier New",monospace',
        fontSize: 12,
        margin: 0,
        background: 'rgba(0,0,0,0.03)',
        padding: 12,
        borderRadius: 8,
      }}
    >
      {text ?? '-'}
    </pre>
  </Typography.Paragraph>
);

export const LogModal = ({ open, log, onClose }: Props) => {
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={1000}
      destroyOnHidden
      title={
        <Space size={8} wrap>
          <span>Лог #{log?.Id ?? '-'}</span>
          {log?.Status && <StatusPill status={log.Status} />}
          {log?.TimestampUtc && (
            <Tag>{moment(log.TimestampUtc).format('DD.MM.YYYY HH:mm')}</Tag>
          )}
        </Space>
      }
    >
      {!log ? null : (
        <>
          <Descriptions
            bordered
            size="small"
            column={2}
            labelStyle={{ width: 220 }}
          >
            <Descriptions.Item label="Организация">
              {log.OrganizationName ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Лог ID">{log.Id}</Descriptions.Item>

            <Descriptions.Item label="Провайдер / Модель">
              {[log.Provider, log.Model].filter(Boolean).join(' ') || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Статус">
              <StatusPill status={log.Status} />
            </Descriptions.Item>

            <Descriptions.Item label="Пользователь (UserId)">
              {log.UserId ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Сущность (EntityId)">
              {log.EntityId ?? '-'}
            </Descriptions.Item>

            <Descriptions.Item label="Токены, вход">
              {log.InputTokens ?? 0}
            </Descriptions.Item>
            <Descriptions.Item label="Токены, исход">
              {log.OutputTokens ?? 0}
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <Typography.Title level={5} style={{ marginTop: 0 }}>
            Request
          </Typography.Title>
          <Pre text={tryPretty(log.Request)} />

          <Divider />

          <Typography.Title level={5}>Response</Typography.Title>
          <Pre text={tryPretty(log.Response)} />
        </>
      )}
    </Modal>
  );
};
